import { useState, useEffect } from 'react';
import { Save, Plus, Trash2, AlertCircle, Edit, Star, RefreshCw, CheckCircle, Clock, User } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const TestimonialsEditor = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [message, setMessage] = useState('');
  const [fetchingReviews, setFetchingReviews] = useState(false);
  const [showGoogleReviews, setShowGoogleReviews] = useState(false);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [placeInfo, setPlaceInfo] = useState(null);
  const [convertingReview, setConvertingReview] = useState(null);
  const [savedReviews, setSavedReviews] = useState([]);
  const [savingReview, setSavingReview] = useState(null);

  useEffect(() => {
    fetchTestimonials();
    fetchSavedReviews();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials`);
      if (response.ok) {
        const data = await response.json();
        setTestimonials(data);
      }
    } catch (error) {
      setMessage('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedReviews = async () => {
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-for-later`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSavedReviews(data);
      }
    } catch (error) {
      console.error('Failed to load saved reviews');
    }
  };

  const handleSave = async (testimonialData) => {
    try {
      const url = testimonialData.id ? 
        `${process.env.REACT_APP_BACKEND_URL}/api/testimonials/${testimonialData.id}` :
        `${process.env.REACT_APP_BACKEND_URL}/api/testimonials`;
      
      const method = testimonialData.id ? 'PUT' : 'POST';
      
      const response = await adminFetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(testimonialData)
      });
      
      if (response.ok) {
        setMessage('Testimonial saved successfully!');
        setEditingTestimonial(null);
        fetchTestimonials();
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setMessage('Failed to save testimonial');
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials/${testimonialId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setMessage('Testimonial deleted successfully!');
        fetchTestimonials();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      setMessage('Failed to delete testimonial');
    }
  };

  const fetchGoogleReviews = async () => {
    setFetchingReviews(true);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/fetch-google-reviews`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        // Check if this is an info message (no new reviews) vs actual reviews
        if (data.detail) {
          // Backend returned a message instead of reviews (all already imported)
          setMessage(data.detail);
          setGoogleReviews([]);
          setShowGoogleReviews(false);
        } else if (data.reviews && data.reviews.length > 0) {
          setGoogleReviews(data.reviews);
          setPlaceInfo({
            name: data.place_name,
            total_reviews: data.total_reviews
          });
          setShowGoogleReviews(true);
          setMessage(`Found ${data.reviews.length} new Google reviews for ${data.place_name} (not yet imported)`);
        } else {
          setMessage('No new reviews available');
          setGoogleReviews([]);
          setShowGoogleReviews(false);
        }
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fetch reviews');
      }
    } catch (error) {
      setMessage(`Error fetching Google reviews: ${error.message}`);
    } finally {
      setFetchingReviews(false);
    }
  };

  const convertGoogleReview = async (review) => {
    setConvertingReview(review.author_name);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/convert-google-review`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(review)
      });
      
      if (response.ok) {
        setMessage(`Successfully converted review from ${review.author_name}!`);
        fetchTestimonials();
        setTimeout(() => setMessage(''), 3000);
        
        // Remove converted review from list
        setGoogleReviews(prev => prev.filter(r => r.author_name !== review.author_name || r.text !== review.text));
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to convert review');
      }
    } catch (error) {
      setMessage(`Error converting review: ${error.message}`);
    } finally {
      setConvertingReview(null);
    }
  };

  const saveForLater = async (review) => {
    setSavingReview(review.author_name);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/save-review-for-later`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          review_time: review.time,
          author_name: review.author_name,
          rating: review.rating,
          text: review.text,
          profile_photo_url: review.profile_photo_url,
          relative_time_description: review.relative_time_description
        })
      });
      
      if (response.ok) {
        setMessage(`Review from ${review.author_name} saved for later!`);
        setTimeout(() => setMessage(''), 3000);
        
        // Remove saved review from Google reviews list
        setGoogleReviews(prev => prev.filter(r => r.time !== review.time));
        
        // Refresh saved reviews list
        fetchSavedReviews();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save review');
      }
    } catch (error) {
      setMessage(`Error saving review: ${error.message}`);
    } finally {
      setSavingReview(null);
    }
  };

  const convertSavedReview = async (savedReview) => {
    setConvertingReview(savedReview.review_time);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/convert-saved-review/${savedReview.review_time}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage(`Successfully converted saved review from ${savedReview.author_name}!`);
        fetchTestimonials();
        fetchSavedReviews();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to convert review');
      }
    } catch (error) {
      setMessage(`Error converting review: ${error.message}`);
    } finally {
      setConvertingReview(null);
    }
  };

  const deleteSavedReview = async (reviewTime) => {
    if (!window.confirm('Are you sure you want to remove this saved review?')) return;
    
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-for-later/${reviewTime}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setMessage('Saved review removed successfully!');
        fetchSavedReviews();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete saved review');
      }
    } catch (error) {
      setMessage(`Error deleting saved review: ${error.message}`);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star 
        key={index} 
        className={`h-4 w-4 ${index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const GoogleReviewsSection = () => (
    <div className="space-y-6">
      <Card className="admin-card admin-corner-accent border-stone-200">
        <CardHeader className="border-b border-stone-200 bg-gradient-to-r from-stone-50 to-amber-50/30 dark:from-stone-800 dark:to-amber-900/20">
          <CardTitle className="flex items-center space-x-2 text-gray-900 dark:text-gray-100">
            <RefreshCw className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <span>Import Google Reviews</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
            <p className="text-blue-800 dark:text-blue-300 text-sm mb-2">
              <strong>Smart Import:</strong> This will fetch your Google reviews and show only the ones
              you haven't imported yet as testimonials or saved for later.
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-400">
              Reviews are tracked by their timestamp, so each review can only be imported once.
            </p>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 p-4 rounded-lg">
            <p className="text-yellow-800 dark:text-yellow-300 text-sm mb-2">
              <strong>Google API Limitation:</strong> Google Places API only returns about 5 most recent reviews per request,
              not all {placeInfo?.total_reviews || 32} total reviews. Fetch reviews periodically to see different ones as they rotate.
            </p>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 p-4 rounded-lg">
            <p className="text-amber-800 dark:text-amber-300 text-sm mb-2">
              <strong>API Key Note:</strong> Your API key has referer restrictions. If this fails,
              you may need to temporarily remove restrictions in Google Cloud Console.
            </p>
          </div>
          
          <Button
            onClick={fetchGoogleReviews}
            disabled={fetchingReviews}
            variant="adminPrimary"
            className="w-full py-6 text-lg font-semibold"
          >
            <RefreshCw className={`h-5 w-5 mr-2 ${fetchingReviews ? 'animate-spin' : ''}`} />
            {fetchingReviews ? 'Fetching Reviews...' : 'Fetch New Google Reviews'}
          </Button>
        </CardContent>
      </Card>

      {showGoogleReviews && googleReviews.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Google Reviews for {placeInfo?.name}</span>
              <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                {googleReviews.length} reviews available • {placeInfo?.total_reviews} total reviews
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {googleReviews.map((review, index) => (
                <Card key={index} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {review.profile_photo_url ? (
                          <img 
                            src={review.profile_photo_url} 
                            alt={review.author_name}
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                          </div>
                        )}
                        <div>
                          <h4 className="font-semibold">{review.author_name}</h4>
                          <div className="flex items-center space-x-2">
                            <div className="flex space-x-1">
                              {renderStars(review.rating)}
                            </div>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {review.relative_time_description}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={() => convertGoogleReview(review)}
                          disabled={convertingReview === review.author_name || savingReview === review.author_name}
                          variant="adminPrimary"
                        >
                          {convertingReview === review.author_name ? (
                            <>
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                              Converting...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Use Now
                            </>
                          )}
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => saveForLater(review)}
                          disabled={convertingReview === review.author_name || savingReview === review.author_name}
                          className="border-blue-500 text-blue-600 hover:bg-blue-50"
                        >
                          {savingReview === review.author_name ? (
                            <>
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Clock className="h-3 w-3 mr-1" />
                              Save for Later
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                      "{review.text}"
                    </p>
                    
                    <div className="text-xs text-gray-500 flex items-center space-x-2">
                      <Clock className="h-3 w-3" />
                      <span>Posted {formatDate(review.time)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const TestimonialForm = ({ testimonial, onSave, onCancel }) => {
    const [testimonialData, setTestimonialData] = useState(testimonial || {
      name: '',
      rating: 5,
      text: '',
      program: ''
    });

    return (
      <Card className="mb-6 admin-card admin-corner-accent border-stone-200">
        <CardHeader className="border-b border-stone-200 bg-gradient-to-r from-stone-50 to-amber-50/30">
          <CardTitle className="text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <span className="text-amber-600">●</span>
            {testimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-gray-800 dark:text-gray-200 font-medium">Customer Name</Label>
              <Input
                id="name"
                value={testimonialData.name}
                onChange={(e) => setTestimonialData({...testimonialData, name: e.target.value})}
                placeholder="Sarah Johnson"
                className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background/80"
              />
            </div>

            <div>
              <Label htmlFor="program" className="text-gray-800 dark:text-gray-200 font-medium">Program</Label>
              <Input
                id="program"
                value={testimonialData.program}
                onChange={(e) => setTestimonialData({...testimonialData, program: e.target.value})}
                placeholder="Youth Program"
                className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background/80"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="rating">Rating</Label>
            <div className="flex items-center space-x-2 mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setTestimonialData({...testimonialData, rating: star})}
                  className="p-1"
                >
                  <Star 
                    className={`h-6 w-6 ${star <= testimonialData.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">{testimonialData.rating} stars</span>
            </div>
          </div>
          
          <div>
            <Label htmlFor="text" className="text-gray-800 dark:text-gray-200 font-medium">Testimonial Text</Label>
            <Textarea
              id="text"
              value={testimonialData.text}
              onChange={(e) => setTestimonialData({...testimonialData, text: e.target.value})}
              placeholder="Share what the customer said about their experience..."
              rows={4}
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background/80"
            />
          </div>

          <div className="flex space-x-2 pt-4 border-t border-stone-200">
            <Button onClick={() => onSave(testimonialData)} variant="adminPrimary">
              <Save className="h-4 w-4 mr-2" />
              Save Testimonial
            </Button>
            <Button variant="adminOutline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Testimonials & Reviews"
        description="Manage customer reviews and testimonials"
        icon={<Star className="h-6 w-6" />}
      />

      {message && (
        <div className={`flex items-center space-x-2 p-4 rounded-lg ${
          message.includes('success') || message.includes('Successfully') || message.includes('Found') ?
          'bg-green-50 text-green-800 border border-green-200' :
          'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <AlertCircle className="h-4 w-4" />
          <span>{message}</span>
        </div>
      )}

      {/* Google Reviews Section */}
      <GoogleReviewsSection />
      
      {/* Saved for Later Section */}
      {savedReviews.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-blue-600" />
              <span>Saved for Later ({savedReviews.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {savedReviews.map((savedReview) => (
                <Card key={savedReview.review_time} className="border-l-4 border-l-yellow-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {savedReview.profile_photo_url ? (
                          <img
                            src={savedReview.profile_photo_url}
                            alt={savedReview.author_name}
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                          </div>
                        )}
                        <div>
                          <h4 className="font-semibold">{savedReview.author_name}</h4>
                          <div className="flex items-center space-x-2">
                            <div className="flex space-x-1">
                              {renderStars(savedReview.rating || 5)}
                            </div>
                            {savedReview.relative_time_description && (
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {savedReview.relative_time_description}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={() => convertSavedReview(savedReview)}
                          disabled={convertingReview === savedReview.review_time}
                          variant="adminPrimary"
                        >
                          {convertingReview === savedReview.review_time ? (
                            <>
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                              Converting...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Use Now
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteSavedReview(savedReview.review_time)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>

                    {savedReview.text ? (
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                        "{savedReview.text}"
                      </p>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 italic mb-2">
                        (No review text available - this is an older saved review)
                      </p>
                    )}

                    <div className="text-xs text-gray-500 flex items-center space-x-2">
                      <Clock className="h-3 w-3" />
                      <span>Saved on {new Date(savedReview.created_at).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Manual Add Section */}
      {!editingTestimonial && (
        <div className="flex justify-end">
          <Button
            onClick={() => setEditingTestimonial({})}
            variant="adminPrimary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Manual Testimonial
          </Button>
        </div>
      )}
      
      {editingTestimonial && (
        <TestimonialForm 
          testimonial={editingTestimonial}
          onSave={handleSave}
          onCancel={() => setEditingTestimonial(null)}
        />
      )}
      
      {/* Current Testimonials */}
      {testimonials.length > 0 && (
        <Card className="admin-card admin-corner-accent border-stone-200">
          <CardHeader className="border-b border-stone-200 bg-gradient-to-r from-stone-50 to-amber-50/30">
            <CardTitle className="text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <span className="text-amber-600">●</span>
              Current Testimonials ({testimonials.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="admin-card border-stone-200 hover:border-amber-500 transition-all">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">{testimonial.name}</h3>
                        <div className="flex space-x-1">
                          {renderStars(testimonial.rating)}
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-3">"{testimonial.text}"</p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                        <span>{testimonial.program}</span>
                      </div>
                      
                      <div className="flex space-x-2 pt-2">
                        <Button
                          size="sm"
                          variant="adminSecondary"
                          onClick={() => setEditingTestimonial(testimonial)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="adminOutline"
                          onClick={() => handleDelete(testimonial.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TestimonialsEditor;